import { CheckCircle2, CircleOff, Clock, ShieldX, XCircle } from "lucide-react";

import type { SnapshotResponse } from "@/api/types";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { RefreshCw } from "lucide-react";

interface Props {
	snapshot: SnapshotResponse | null;
	onPoll?: () => void;
	isPolling?: boolean;
}

function formatRelative(iso: string): string {
	const then = new Date(iso).getTime();
	const now = Date.now();
	const seconds = Math.max(0, Math.round((now - then) / 1000));
	if (seconds < 60) return `${seconds}s ago`;
	const minutes = Math.floor(seconds / 60);
	if (minutes < 60) return `${minutes}m ago`;
	const hours = Math.floor(minutes / 60);
	if (hours < 24) return `${hours}h ago`;
	const days = Math.floor(hours / 24);
	return `${days}d ago`;
}

/** Health-poll outcome: ok | error | unauthorized | timeout, plus latency
 * + last-polled-at. Renders "Never polled" when there's no snapshot yet. */
export function SnapshotStatusBadge({ snapshot, onPoll, isPolling }: Props) {
	const badge = (() => {
		if (!snapshot) {
			return (
				<Badge variant="outline" className="text-muted-foreground">
					<CircleOff className="mr-1 h-3 w-3" aria-hidden />
					Never polled
				</Badge>
			);
		}
		switch (snapshot.status) {
			case "ok":
				return (
					<Badge variant="success">
						<CheckCircle2 className="mr-1 h-3 w-3" aria-hidden />
						OK
					</Badge>
				);
			case "timeout":
				return (
					<Badge variant="destructive">
						<Clock className="mr-1 h-3 w-3" aria-hidden />
						Timeout
					</Badge>
				);
			case "unauthorized":
				return (
					<Badge variant="destructive">
						<ShieldX className="mr-1 h-3 w-3" aria-hidden />
						Unauthorized
					</Badge>
				);
			default:
				return (
					<Badge variant="destructive">
						<XCircle className="mr-1 h-3 w-3" aria-hidden />
						Error
					</Badge>
				);
		}
	})();

	return (
		<div className="flex flex-wrap items-center gap-2 text-xs">
			{badge}
			{snapshot?.polled_at ? (
				<span
					className="text-muted-foreground"
					title={`Polled ${new Date(snapshot.polled_at).toLocaleString()}`}
				>
					{formatRelative(snapshot.polled_at)}
				</span>
			) : null}
			{snapshot?.latency_ms != null ? (
				<span className="text-muted-foreground" title="Round-trip latency to the portal">
					{snapshot.latency_ms}ms
				</span>
			) : null}
			{snapshot?.error_message ? (
				<span className="text-muted-foreground italic line-clamp-1" title={snapshot.error_message}>
					— {snapshot.error_message}
				</span>
			) : null}
			{onPoll ? (
				<Button
					size="sm"
					variant="ghost"
					className="h-6 px-2"
					onClick={onPoll}
					disabled={isPolling}
					title="Poll now"
					aria-label="Poll now"
				>
					<RefreshCw className={`h-3 w-3 ${isPolling ? "animate-spin" : ""}`} aria-hidden />
				</Button>
			) : null}
		</div>
	);
}
